import React, { useState } from "react";
import api from "../lib/api";
import { Input, Button, Spinner } from "../components/ui";
import { Home, Search, MapPin, AlertTriangle } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

// Quick-add phrases for the free-text box. Kept as fixed German strings rather
// than i18n keys — like the Faktor-method domain terms elsewhere, these are the
// query language itself (what a listing description says), not UI chrome, so
// translating them would drift from what actually matches. Grouped for
// readability; not exhaustive — the underlying search accepts any free text.
const CHIP_GROUPS = [
  { key: "features", chips: ["mit Garten", "mit Balkon", "mit Terrasse", "mit Sauna", "mit Pool", "mit Garage", "mit Keller"] },
  { key: "location", chips: ["am See", "mit Seezugang"] },
  { key: "rooms", chips: ["2 Zimmer", "3 Zimmer", "4 Zimmer", "5 Zimmer"] },
  { key: "exclude", chips: ["kein Neubau", "kein Reihenhaus"] },
];

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export default function PersonalSearch() {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [intention, setIntention] = useState("kauf");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const runSearch = async () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.post("/personal-search", { query: query.trim(), intention });
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || t("personalSearch.searchFailed"));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") runSearch();
  };

  const isChipActive = (chip) => query.toLowerCase().includes(chip.toLowerCase());

  const toggleChip = (chip) => {
    if (isChipActive(chip)) {
      const next = query
        .replace(new RegExp(escapeRegExp(chip), "i"), "")
        .replace(/\s+,/g, ",")       // "word ," left behind -> "word,"
        .replace(/,\s*,/g, ",")      // collapse a doubled separator left behind
        .replace(/^[\s,]+|[\s,]+$/g, "")
        .replace(/\s{2,}/g, " ");
      setQuery(next);
    } else {
      setQuery((prev) => {
        const trimmed = prev.trim();
        return trimmed ? `${trimmed}, ${chip}` : chip;
      });
    }
  };

  const listings = result?.listings || [];
  const unresolved = result?.unresolved_locations || [];

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-6 border-b border-border bg-grid">
        <div className="max-w-3xl">
          <div className="overline">{t("personalSearch.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("personalSearch.title")}</h1>
          <p className="mt-2 text-xs text-text-muted max-w-2xl leading-relaxed">
            {t("personalSearch.subtitle")}
          </p>
        </div>
      </div>

      <div className="p-6 max-w-3xl">
        <div className="inline-flex border border-border mb-3" role="group" aria-label={t("personalSearch.intentionLabel")}>
          {["kauf", "miete"].map((val) => {
            const active = intention === val;
            return (
              <button
                key={val}
                type="button"
                data-testid={`personalsearch-intention-${val}`}
                aria-pressed={active}
                onClick={() => setIntention(val)}
                className={`px-4 py-1.5 text-xs font-mono uppercase tracking-widest2 transition-colors ${
                  active ? "bg-signal-green text-black" : "text-text-muted hover:text-white"
                }`}
              >
                {t(val === "kauf" ? "personalSearch.kauf" : "personalSearch.miete")}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="personalsearch-query"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("personalSearch.placeholder")}
            className="flex-1"
          />
          <Button variant="green" onClick={runSearch} disabled={loading || !query.trim()}>
            {loading ? <Spinner size={14} /> : <Search size={14} />}
            {t("personalSearch.searchButton")}
          </Button>
        </div>

        <div className="mt-3 space-y-2">
          <div className="overline text-text-muted">{t("personalSearch.chipsLabel")}</div>
          {CHIP_GROUPS.map((group) => (
            <div key={group.key} className="flex flex-wrap gap-1.5">
              {group.chips.map((chip) => {
                const active = isChipActive(chip);
                return (
                  <button
                    key={chip}
                    type="button"
                    data-testid={`personalsearch-chip-${chip}`}
                    aria-pressed={active}
                    onClick={() => toggleChip(chip)}
                    className={`px-2.5 py-1 text-[11px] font-mono border transition-colors ${
                      active
                        ? "border-signal-green bg-signal-green/10 text-signal-green"
                        : "border-border text-text-muted hover:text-white hover:border-white"
                    }`}
                  >
                    {chip}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {err && (
          <div className="mt-4 border border-signal-red/40 bg-signal-red/10 px-4 py-3 text-sm text-signal-red">
            {err}
          </div>
        )}

        {result && unresolved.length > 0 && (
          <div className="mt-4 flex items-start gap-2 border border-signal-yellow/40 bg-signal-yellow/10 px-4 py-3 text-xs text-signal-yellow">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>{t("personalSearch.unresolvedLocations", { locations: unresolved.join(", ") })}</span>
          </div>
        )}

        {result && listings.length === 0 && !err && (
          <div className="mt-6 text-sm text-text-muted font-mono">
            {/* Four states, most specific first. Each earlier one is a strictly
                better explanation than the next: "no place in the query" beats
                "no URL for the locations found" (there were none), which beats
                "the portal returned nothing" (it was never asked). */}
            {result.had_locations === false
              ? t("personalSearch.noLocationInQuery")
              : result.searched_urls === 0
              ? t("personalSearch.noFetchAttempted")
              : result.raw_fetched === 0
                ? t("personalSearch.noResultsPortalEmpty")
                : t("personalSearch.noResults")}
          </div>
        )}

        {/* The facts behind a zero-result answer. Rendered only when nothing came
            back, because that is the only time the cause is ambiguous: a soft
            block, a portal redesign and an empty search are identical from the
            outside. Each row shows what the fetch actually returned. */}
        {result && listings.length === 0 && !err
          && (result.fetch_diag || []).length > 0 && (
          <div className="mt-4 border border-border rounded-sm p-3 font-mono text-xs">
            <div className="overline mb-2">{t("personalSearch.fetchDiagTitle")}</div>
            {result.fetch_diag.map((d, i) => {
              const redirected = d.final_url && !d.url.startsWith(d.final_url)
                && d.final_url !== d.url;
              const hint = d.error ? null
                : redirected || (d.bytes != null && d.bytes < 50000)
                  ? t("personalSearch.fetchDiagHintBlock")
                  : d.parsed_page1 === 0
                    ? t("personalSearch.fetchDiagHintParser")
                    : t("personalSearch.fetchDiagHintEmpty");
              return (
                <div key={i} className="mb-2 last:mb-0">
                  <div className="text-text-muted break-all">{d.url}</div>
                  <div className="text-text-secondary">
                    {d.error
                      ? d.error
                      : `${d.status ?? "—"} · ${d.bytes ?? "—"} bytes · `
                        + `${d.parsed_page1 ?? "—"} parsed`
                        + (redirected ? ` · ${t("personalSearch.fetchDiagRedirect")} → ${d.final_url}` : "")}
                  </div>
                  {hint && <div className="text-signal-green mt-0.5">{hint}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {listings.length > 0 && (
        <div className="px-6 pb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl">
          {listings.map((l) => (
            <ListingCard key={`${l.platform}-${l.listing_id}`} listing={l} t={t} intention={intention} />
          ))}
        </div>
      )}
    </div>
  );
}

function ListingCard({ listing, t, intention }) {
  return (
    <a
      href={listing.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block border border-border bg-surface hover:border-white transition-colors group"
    >
      <div className="aspect-video bg-bg overflow-hidden">
        {listing.image_url ? (
          <img
            src={listing.image_url}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-text-muted">
            <Home size={24} />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="text-sm text-white leading-snug line-clamp-2">{listing.title}</div>
        <div className="mt-2 flex items-center gap-1.5 text-[11px] font-mono text-text-muted">
          <MapPin size={11} />
          {listing.location || "—"}
        </div>
        <div className="mt-3 flex items-end justify-between">
          <div className="num text-lg text-white">
            {listing.price || "—"}
            {intention === "miete" && listing.price && (
              <span className="text-[10px] font-mono text-text-muted">{t("personalSearch.perMonth")}</span>
            )}
          </div>
          <div className="text-[11px] font-mono text-text-muted text-right">
            {listing.size_m2 ? `${listing.size_m2} m²` : ""}
            {listing.rooms ? ` · ${listing.rooms} ${t("personalSearch.rooms")}` : ""}
          </div>
        </div>
        {listing.feature_matches > 0 && (
          <div className="mt-2 inline-block text-[10px] font-mono uppercase tracking-widest2 text-signal-green border border-signal-green/40 px-2 py-0.5">
            {t("personalSearch.featureMatches", { count: listing.feature_matches })}
          </div>
        )}
      </div>
    </a>
  );
}
